import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useGlobalManualLocationOrder } from "../../../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { sortByLocationOrder } from "../../../../utils/multiColumnSorting";
import { MAX_OPEN_SHIFT_DURATION_HOURS } from "../../../../utils/openShiftDuration";
import { getUniqueLocationNames } from "../../../../utils/utilFunctions";
import { FilterFieldRow } from "../../../UIToolkit/Filters/FilterFieldRow";
import { SystemDropdown } from "../../../UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { RoleGroupOrJobSelector } from "../../../UIToolkit/molecules/RoleGroupOrJobSelector";
import { ShiftDetailedTimeViewWithPicker } from "../../../UIToolkit/molecules/ShiftDetailedTimeViewWithPicker";
import { GapsPostIncentiveField } from "./GapsPostIncentiveField";
import type { GapsPostShiftState } from "./useGapsPostShift";

type Props = {
	form: GapsPostShiftState;
	date: DateTime;
	testIdPrefix: string;
};

/**
 * The post sheet's fields, each under the same small-caps label (`FilterFieldRow`) — the job (or role groups), the
 * hours, the unit when the department posts by unit, and the pick-up incentive when bonuses are on — so the pickers drop
 * their floating labels and name their empty state instead.
 */
export const GapsPostShiftFields: FC<Props> = ({ form, date, testIdPrefix }) => {
	const { t } = useTranslation();
	const { chosenDepartment, isMedicalDepartment } = useHospitalData();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const isPickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const showLocation = isPickupByLocation && !isMedicalDepartment;
	// The classic post screen's own unit list (`PostShift`), so the sheet that replaces it offers exactly the same units,
	// in the same manual-aware location order.
	const locations = useMemo<string[]>(
		() => (showLocation ? sortByLocationOrder(getUniqueLocationNames(chosenDepartment.locations), customStringOrderMap) : []),
		[showLocation, chosenDepartment.locations, customStringOrderMap],
	);
	const selectedLocation = form.location === "" ? undefined : form.location;

	return (
		<Box sx={{ px: 2, pt: 2.25, pb: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
			<FilterFieldRow label={form.postsByJob ? t("gaps.postSheet.job") : t("gaps.postSheet.roleGroups")} dataTestId={`${testIdPrefix}-job`}>
				<RoleGroupOrJobSelector
					singleJob
					fullWidth
					hideLabel
					placeholder={form.postsByJob ? t("gaps.postSheet.jobPlaceholder") : t("gaps.postSheet.roleGroupsPlaceholder")}
					selectedJobIds={form.jobId === "" ? [] : [form.jobId]}
					// An emptied single pick is "no job chosen", the picker's own empty state.
					onSelectedJobsChanged={(ids) => form.setJobId(ids.length === 0 ? "" : ids[0])}
					selectedRoleGroups={form.roleGroups}
					onSelectedRoleGroupsChanged={form.setRoleGroups}
				/>
			</FilterFieldRow>

			<FilterFieldRow label={t("gaps.postSheet.hours")} dataTestId={`${testIdPrefix}-hours`}>
				<ShiftDetailedTimeViewWithPicker
					shiftIndex={form.shiftNo}
					setShiftIndex={form.setShiftNo}
					selectedRoleForShift={form.selectedRoles[0]}
					shiftStart={form.shiftStart}
					shiftEnd={form.shiftEnd}
					setShiftStart={form.setShiftStart}
					setShiftEnd={form.setShiftEnd}
					date={date}
					roleSource="role"
					selectedLocation={selectedLocation}
					selectedRoleGroups={form.roleGroups}
					enabled={!form.isPosting}
					initialTimes={form.initialTimes}
					jobId={form.postsByJob ? form.jobId : undefined}
					hideLabel
					// Chips for a finger in the hours menu, as the staff sheet's hours step draws them.
					editorAppearance="touch"
					maximumDurationHours={MAX_OPEN_SHIFT_DURATION_HOURS}
				/>
			</FilterFieldRow>

			{showLocation && (
				<FilterFieldRow label={t("gaps.postSheet.location")} dataTestId={`${testIdPrefix}-location-field`}>
					<SystemDropdown<string>
						placeholder={t("gaps.postSheet.anyUnit")}
						items={locations}
						// No selection (not an empty-string one) while it is "any unit", so the dropdown shows its placeholder.
						selectedItem={selectedLocation}
						// Cleared is "any unit" — the classic screen posts no location for it.
						onChange={(value) => form.setLocation(value === undefined ? "" : value)}
						clearable
						fullWidth
						data-testid={`${testIdPrefix}-location`}
					/>
				</FilterFieldRow>
			)}

			{isShiftBonusesEnabled && (
				<GapsPostIncentiveField
					incentive={form.incentive}
					onIncentiveChange={form.setIncentive}
					bonusText={form.bonusText}
					onBonusTextChange={form.setBonusText}
					bonus={form.bonus}
					isBonusValid={form.isBonusValid}
					neededNurses={form.neededNurses}
					testIdPrefix={testIdPrefix}
				/>
			)}
		</Box>
	);
};
